import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  CheckCheck,
  ChevronRight,
  CircleAlert,
  Clock3,
  ExternalLink,
  LocateFixed,
  MailCheck,
  MessageSquarePlus,
  Phone,
  RefreshCw,
  Send,
  ShieldAlert,
  Siren,
  Users,
  X,
} from 'lucide-react';
import { api } from '@/services/api';
import { useAdminNotifications } from './use-admin-notifications';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/ui/field';
import { Select } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { Drawer } from '@/components/ui/drawer';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { useToast } from '@/components/ui/toast';
import { PageHeader } from '@/components/ui/page-header';
import { Badge } from '@/components/ui/badge';

const categoryOptions = [
  { key: '', label: 'All', icon: Bell },
  { key: 'unread', label: 'Unread', icon: MailCheck },
  { key: 'emergency', label: 'Emergency', icon: Siren },
  { key: 'safety', label: 'Safety', icon: ShieldAlert },
  { key: 'convoy', label: 'Convoy', icon: Users },
  { key: 'club', label: 'Club', icon: Users },
  { key: 'system', label: 'System', icon: CircleAlert },
];

const severityTone = {
  info: 'accent',
  attention: 'warning',
  critical: 'error',
  emergency: 'error',
};

const statusTone = {
  open: 'warning',
  acknowledged: 'accent',
  resolved: 'success',
  expired: 'neutral',
};

function relativeTime(value) {
  const date = new Date(value);
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return date.toLocaleDateString();
}

function notificationIcon(item) {
  if (item.severity === 'emergency') return Siren;
  if (item.category === 'safety') return ShieldAlert;
  if (item.category === 'convoy') return Users;
  if (item.category === 'club') return Users;
  if (item.category === 'system') return CircleAlert;
  return Bell;
}

function notificationColor(item) {
  if (item.severity === 'emergency') return 'border-rose-400/35 bg-rose-400/[.055] text-rose-300';
  if (item.severity === 'critical') return 'border-orange-400/30 bg-orange-400/[.045] text-orange-300';
  if (item.severity === 'attention') return 'border-amber-300/25 bg-amber-300/[.04] text-amber-200';
  return 'border-cyan-300/18 bg-cyan-300/[.035] text-cyan-200';
}

export function NotificationsPage() {
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [composeOpen, setComposeOpen] = useState(false);
  const [memberIds, setMemberIds] = useState([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState('admin_message');
  const [messageCategory, setMessageCategory] = useState('info');
  const [messageSeverity, setMessageSeverity] = useState('info');
  const [sending, setSending] = useState(false);
  const filters = useMemo(() => ({
    q: search.trim(),
    category: ['unread', 'emergency'].includes(category) ? '' : category,
    severity: category === 'emergency' ? 'emergency' : severity,
    unread: category === 'unread' ? 'true' : '',
    status,
  }), [category, search, severity, status]);
  const query = useAdminNotifications(filters);
  const members = useQuery({
    queryKey: ['member-options'],
    queryFn: () => api('/api/v1/admin/members?limit=100').then((result) => result.data),
  });
  const client = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const items = query.data?.items ?? [];
  const summary = query.data?.summary ?? {};
  const selectedLive = items.find((item) => item._id === selected?._id) ?? selected;
  const options = useMemo(() => (members.data ?? [])
    .filter((member) => member.status !== 'deleted')
    .map((member) => ({
      value: member._id,
      label: member.name,
      description: `${member.email} · @${member.username}`,
      status: member.status === 'active' ? 'online' : 'offline',
    })), [members.data]);

  const counts = useMemo(() => ({
    '': summary.total ?? items.length,
    unread: summary.unread ?? items.filter((item) => !item.readAt).length,
    emergency: summary.emergency ?? items.filter((item) => item.severity === 'emergency').length,
    safety: items.filter((item) => item.category === 'safety').length,
    convoy: items.filter((item) => item.category === 'convoy').length,
    club: items.filter((item) => item.category === 'club').length,
    system: items.filter((item) => item.category === 'system').length,
  }), [items, summary]);

  const update = async (notification, action) => {
    try {
      const result = await api(`/api/v1/admin/notifications/${notification._id}`, {
        method: 'PATCH',
        body: JSON.stringify({ action }),
      });
      setSelected(result.data);
      await client.invalidateQueries({ queryKey: ['admin-notifications'] });
      toast.show(action === 'resolve' ? 'Notification resolved' : action === 'acknowledge' ? 'Notification acknowledged' : 'Notification updated', 'success');
    } catch (error) {
      toast.show(error.message, 'error', 'Update failed');
    }
  };

  const runBulk = async (action) => {
    if (!selectedIds.size) return;
    try {
      await api('/api/v1/admin/notifications/bulk', {
        method: 'POST',
        body: JSON.stringify({ notificationIds: [...selectedIds], action }),
      });
      setSelectedIds(new Set());
      await client.invalidateQueries({ queryKey: ['admin-notifications'] });
      toast.show(`${action.replaceAll('_', ' ')} applied`, 'success');
    } catch (error) {
      toast.show(error.message, 'error', 'Bulk update failed');
    }
  };

  const markAllRead = async () => {
    try {
      await api('/api/v1/admin/notifications/read-all', { method: 'POST' });
      await client.invalidateQueries({ queryKey: ['admin-notifications'] });
      toast.show('All notifications marked as read', 'success');
    } catch (error) {
      toast.show(error.message, 'error', 'Update failed');
    }
  };

  const requestSafeCheck = async (item) => {
    const tripId = item.tripId ?? item.data?.tripId;
    const memberId = item.subjectMemberId ?? item.data?.memberId;
    if (!tripId || !memberId) return;
    try {
      await api(`/api/v1/admin/trips/${tripId}/members/${memberId}/safe-check`, { method: 'POST' });
      toast.show('Safety check sent', 'success');
    } catch (error) {
      toast.show(error.message, 'error', 'Safe check failed');
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const result = await api('/api/v1/admin/notifications', {
        method: 'POST',
        body: JSON.stringify({
          memberIds,
          title: title.trim(),
          body: body.trim(),
          type,
          category: messageCategory,
          severity: messageSeverity,
        }),
      });
      setTitle('');
      setBody('');
      setMemberIds([]);
      setComposeOpen(false);
      await client.invalidateQueries({ queryKey: ['admin-notifications'] });
      toast.show(`Sent to ${result.data.count} rider${result.data.count === 1 ? '' : 's'}`, 'success', 'Delivered');
    } catch (error) {
      toast.show(error.message, 'error', 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const toggleSelected = (id) => setSelectedIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return <div className="workspace-page">
    <PageHeader
      eyebrow="Action center"
      title="Notifications"
      meta={<div className="flex gap-2"><Badge tone="warning">{summary.open ?? 0} open</Badge><Badge tone="error">{summary.emergency ?? 0} emergency</Badge></div>}
      actions={<div className="flex gap-2"><Button variant="secondary" onClick={() => void markAllRead()}><CheckCheck size={14}/>Mark all read</Button><Button onClick={() => setComposeOpen(true)}><MessageSquarePlus size={14}/>New message</Button></div>}
    />

    <div className="workspace-fill grid overflow-hidden border border-white/8 bg-[#07101a]/85 xl:grid-cols-[190px_minmax(340px,.82fr)_minmax(380px,1.18fr)]">
      <aside className="border-b border-white/8 p-3 xl:border-b-0 xl:border-r">
        <p className="mb-2 px-2 text-[9px] font-black uppercase tracking-[.18em] text-slate-600">Categories</p>
        <div className="space-y-1">
          {categoryOptions.map(({ key, label, icon: Icon }) => <button key={key || 'all'} type="button" onClick={() => { setCategory(key); setSelected(undefined); }} className={`flex h-10 w-full items-center gap-2 border px-3 text-xs transition ${category === key ? 'border-cyan-300/25 bg-cyan-300/[.07] text-cyan-100' : 'border-transparent text-slate-500 hover:border-white/8 hover:bg-white/[.025] hover:text-slate-200'}`}>
            <Icon size={14}/><span className="font-semibold">{label}</span><span className="ml-auto font-mono text-[10px]">{counts[key] ?? 0}</span>
          </button>)}
        </div>
        <div className="mt-5 space-y-3 border-t border-white/8 pt-4">
          <Field label="Status"><Select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">All status</option><option value="open">Open</option><option value="acknowledged">Acknowledged</option><option value="resolved">Resolved</option><option value="expired">Expired</option></Select></Field>
          <Field label="Severity"><Select value={severity} onChange={(event) => setSeverity(event.target.value)}><option value="">All severity</option><option value="info">Info</option><option value="attention">Attention</option><option value="critical">Critical</option><option value="emergency">Emergency</option></Select></Field>
        </div>
      </aside>

      <section className="flex min-h-0 flex-col border-b border-white/8 xl:border-b-0 xl:border-r">
        <div className="flex items-center gap-2 border-b border-white/8 p-3">
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search notifications" className="h-9 flex-1 text-xs"/>
          <Button variant="secondary" className="h-9 px-3" onClick={() => query.refetch()}><RefreshCw size={13} className={query.isFetching ? 'animate-spin' : ''}/></Button>
        </div>
        {selectedIds.size > 0 && <div className="flex flex-wrap items-center gap-2 border-b border-cyan-300/15 bg-cyan-300/[.04] px-3 py-2"><span className="text-[10px] font-black uppercase tracking-[.14em] text-cyan-200">{selectedIds.size} selected</span><Button variant="secondary" className="h-7 px-2 text-[10px]" onClick={() => void runBulk('mark_read')}>Mark read</Button><Button variant="secondary" className="h-7 px-2 text-[10px]" onClick={() => void runBulk('acknowledge')}>Acknowledge</Button><Button variant="secondary" className="h-7 px-2 text-[10px]" onClick={() => void runBulk('resolve')}>Resolve</Button><button type="button" className="ml-auto text-slate-500" onClick={() => setSelectedIds(new Set())}><X size={14}/></button></div>}
        <div className="scrollbar min-h-0 flex-1 overflow-y-auto">
          {query.isLoading ? <div className="space-y-2 p-3">{Array.from({ length: 7 }).map((_, index) => <div key={index} className="h-20 animate-pulse bg-white/[.035]"/>)}</div> : items.length === 0 ? <EmptyState title="No notifications"/> : items.map((item) => {
            const Icon = notificationIcon(item);
            const active = selectedLive?._id === item._id;
            return <button key={item._id} type="button" onClick={() => setSelected(item)} className={`flex w-full items-start gap-3 border-b border-white/6 p-3 text-left transition ${active ? 'bg-cyan-300/[.055]' : 'hover:bg-white/[.025]'}`}>
              <span onClick={(event) => event.stopPropagation()} className="pt-1"><input aria-label="Select notification" type="checkbox" checked={selectedIds.has(item._id)} onChange={() => toggleSelected(item._id)} className="size-3.5 accent-cyan-300"/></span>
              <span className={`grid size-9 shrink-0 place-items-center border ${notificationColor(item)}`}><Icon size={16}/></span>
              <span className="min-w-0 flex-1"><span className="flex items-start gap-2"><span className="min-w-0 flex-1 truncate text-xs font-bold text-slate-200">{item.title}</span>{!item.readAt && <span className="mt-1 size-1.5 shrink-0 bg-cyan-300 shadow-[0_0_8px_#67e8f9]"/>}</span><span className="mt-1 block line-clamp-2 text-[11px] leading-4 text-slate-500">{item.body}</span><span className="mt-2 flex items-center gap-2 text-[9px] text-slate-700"><Clock3 size={10}/>{relativeTime(item.createdAt)}<Badge tone={statusTone[item.status] ?? 'neutral'}>{item.status}</Badge></span></span>
              <ChevronRight size={14} className="mt-3 shrink-0 text-slate-700"/>
            </button>;
          })}
        </div>
      </section>

      <section className="scrollbar min-h-0 overflow-y-auto p-5">
        {!selectedLive ? <div className="grid h-full min-h-80 place-items-center text-center"><div><Bell size={34} className="mx-auto text-slate-700"/><p className="mt-4 text-sm font-bold text-slate-300">Select a notification</p><p className="mt-1 text-xs text-slate-600">Details and actions appear here.</p></div></div> : <NotificationDetail
          item={selectedLive}
          onUpdate={(action) => void update(selectedLive, action)}
          onSafeCheck={() => void requestSafeCheck(selectedLive)}
          onLocate={() => navigate(`/live-monitoring?tripId=${selectedLive.tripId ?? selectedLive.data?.tripId ?? ''}&memberId=${selectedLive.subjectMemberId ?? selectedLive.data?.memberId ?? ''}`)}
          onOpenConvoy={() => navigate(`/convoys?tripId=${selectedLive.tripId ?? selectedLive.data?.tripId ?? ''}`)}
        />}
      </section>
    </div>

    <Drawer open={composeOpen} title="New notification" eyebrow="Broadcast" onClose={() => setComposeOpen(false)}>
      <div className="space-y-4">
        <Field label="Recipients" hint={`${memberIds.length} selected`} required><MultiSelect options={options} value={memberIds} onChange={setMemberIds} placeholder={members.isLoading ? 'Loading riders...' : 'Select riders'}/></Field>
        <div className="grid grid-cols-2 gap-3"><Field label="Category"><Select value={messageCategory} onChange={(event) => setMessageCategory(event.target.value)}><option value="info">Info</option><option value="safety">Safety</option><option value="convoy">Convoy</option><option value="club">Club</option><option value="media">Media</option><option value="system">System</option></Select></Field><Field label="Severity"><Select value={messageSeverity} onChange={(event) => setMessageSeverity(event.target.value)}><option value="info">Info</option><option value="attention">Attention</option><option value="critical">Critical</option><option value="emergency">Emergency</option></Select></Field></div>
        <Field label="Message type"><Select value={type} onChange={(event) => setType(event.target.value)}><option value="admin_message">Admin message</option><option value="trip_alert">Trip alert</option><option value="security_notice">Security notice</option><option value="system_update">System update</option><option value="maintenance">Maintenance</option></Select></Field>
        <Field label="Title" required><Input maxLength={120} placeholder="Clear, short title" value={title} onChange={(event) => setTitle(event.target.value)}/></Field>
        <Field label="Message" hint={`${body.length}/1000`} required><Textarea maxLength={1000} placeholder="Write a concise message" value={body} onChange={(event) => setBody(event.target.value)}/></Field>
        <div className="border border-white/8 bg-black/12 p-3"><p className="text-[9px] font-bold uppercase tracking-[.15em] text-slate-600">Preview</p><p className="mt-2 text-sm font-semibold text-slate-200">{title || 'Notification title'}</p><p className="mt-1 text-xs leading-5 text-slate-500">{body || 'Message preview appears here.'}</p></div>
        <Button className="w-full" disabled={!memberIds.length || title.trim().length < 2 || body.trim().length < 2 || sending} onClick={() => void send()}><Send size={15}/>Send to {memberIds.length || 0}</Button>
      </div>
    </Drawer>
    <LoadingOverlay open={sending} label="Sending notification"/>
  </div>;
}

function NotificationDetail({ item, onUpdate, onSafeCheck, onLocate, onOpenConvoy }) {
  const Icon = notificationIcon(item);
  const rider = item.subjectMember ?? item.recipient;
  const phone = rider?.phoneNumber;
  const hasRideContext = Boolean(item.tripId ?? item.data?.tripId);
  return <div className="space-y-5">
    <div className="flex items-start gap-3"><span className={`grid size-11 shrink-0 place-items-center border ${notificationColor(item)}`}><Icon size={20}/></span><div className="min-w-0 flex-1"><div className="flex flex-wrap gap-2"><Badge tone={severityTone[item.severity] ?? 'neutral'}>{item.severity}</Badge><Badge tone={statusTone[item.status] ?? 'neutral'}>{item.status}</Badge><Badge>{item.category}</Badge></div><h2 className="mt-3 text-xl font-black text-slate-100">{item.title}</h2><p className="mt-2 text-sm leading-6 text-slate-400">{item.body}</p></div></div>
    <div className="grid grid-cols-2 gap-2 border-y border-white/8 py-4 text-xs"><Info label="Rider" value={rider?.name ?? item.data?.memberName ?? '—'}/><Info label="Convoy" value={item.trip?.name ?? '—'}/><Info label="Created" value={new Date(item.createdAt).toLocaleString()}/><Info label="Type" value={String(item.type ?? '').replaceAll('_', ' ')}/></div>
    {item.data?.lastReliablePosition && <div className="border border-white/8 bg-black/12 p-3"><p className="text-[9px] font-black uppercase tracking-[.15em] text-slate-600">Last reliable position</p><p className="mt-2 font-mono text-xs text-slate-300">{Number(item.data.lastReliablePosition.latitude).toFixed(6)}, {Number(item.data.lastReliablePosition.longitude).toFixed(6)}</p><p className="mt-1 text-[10px] text-slate-600">Speed {Math.round(item.data.lastReliablePosition.speedKmh ?? 0)} km/h · Accuracy {Math.round(item.data.lastReliablePosition.accuracyMeters ?? 0)} m</p></div>}
    <div className="grid grid-cols-2 gap-2">
      {hasRideContext && <Button variant="secondary" onClick={onLocate}><LocateFixed size={14}/>Locate rider</Button>}
      {hasRideContext && <Button variant="secondary" onClick={onOpenConvoy}><ExternalLink size={14}/>Open convoy</Button>}
      {hasRideContext && item.category === 'safety' && <Button variant="secondary" onClick={onSafeCheck}><AlertTriangle size={14}/>Safe check</Button>}
      {phone && <a href={`tel:${phone}`} className="flex h-10 items-center justify-center gap-2 border border-white/10 text-xs font-bold text-slate-300 hover:border-cyan-300/30 hover:text-cyan-100"><Phone size={14}/>Call rider</a>}
    </div>
    <div className="border-t border-white/8 pt-4"><p className="mb-3 text-[9px] font-black uppercase tracking-[.16em] text-slate-600">Workflow</p><div className="flex flex-wrap gap-2">{item.status === 'open' && <Button onClick={() => onUpdate('acknowledge')}><MailCheck size={14}/>Acknowledge</Button>}{['open', 'acknowledged'].includes(item.status) && <Button variant="secondary" onClick={() => onUpdate('resolve')}><CheckCheck size={14}/>Resolve</Button>}{['resolved', 'expired'].includes(item.status) && <Button variant="secondary" onClick={() => onUpdate('reopen')}><RefreshCw size={14}/>Reopen</Button>}</div></div>
  </div>;
}

function Info({ label, value }) {
  return <div><p className="text-[9px] font-black uppercase tracking-[.14em] text-slate-700">{label}</p><p className="mt-1 truncate font-medium text-slate-300">{value}</p></div>;
}
